import { useEffect, useState } from "react";
import { Download, Share, PlusSquare, X, WifiOff, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/lib/i18n";
import { flushQueue, useOnline, useQueue } from "@/lib/offline";

let deferredPrompt = null;
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => { e.preventDefault(); deferredPrompt = e; window.dispatchEvent(new Event("mohassib:installable")); });
  window.addEventListener("appinstalled", () => { deferredPrompt = null; window.dispatchEvent(new Event("mohassib:installable")); });
}
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;

export function InstallButton({ className = "" }) {
  const { t } = useLang();
  const [, tick] = useState(0);
  const [guide, setGuide] = useState(false);
  useEffect(() => { const h = () => tick((x) => x + 1); window.addEventListener("mohassib:installable", h); return () => window.removeEventListener("mohassib:installable", h); }, []);
  if (isStandalone()) return null;
  const ios = isIOS();
  if (!deferredPrompt && !ios) return null;
  const install = async () => {
    if (deferredPrompt) { deferredPrompt.prompt(); const { outcome } = await deferredPrompt.userChoice; if (outcome === "accepted") toast.success(t("installDone")); deferredPrompt = null; tick((x) => x + 1); }
    else setGuide(true);
  };
  return (
    <>
      <button data-testid="install-app-btn" onClick={install} className={`flex items-center gap-2 rounded-lg border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-3 py-2 text-xs font-bold text-[#F0D77A] transition-colors hover:bg-[#D4AF37]/20 ${className}`}>
        <Download className="h-3.5 w-3.5" />{t("installApp")}
      </button>
      {guide && (
        <div data-testid="ios-install-guide" className="fixed inset-0 z-[100] flex items-end justify-center bg-black/60 p-4 sm:items-center" onClick={() => setGuide(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 text-slate-900 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between"><h3 className="font-display text-lg font-bold">{t("installApp")}</h3><button data-testid="ios-guide-close" onClick={() => setGuide(false)}><X className="h-5 w-5 text-slate-400" /></button></div>
            <ol className="mt-4 space-y-3 text-sm">
              <li className="flex items-center gap-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 font-bold">1</span>{t("iosStep1")} <Share className="h-4 w-4 text-[#007AFF]" /></li>
              <li className="flex items-center gap-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 font-bold">2</span>{t("iosStep2")} <PlusSquare className="h-4 w-4" /></li>
              <li className="flex items-center gap-3"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 font-bold">3</span>{t("iosStep3")}</li>
            </ol>
            <p className="mt-4 text-xs text-slate-500">{t("iosSafariNote")}</p>
          </div>
        </div>
      )}
    </>
  );
}

export function OfflineBar() {
  const { t } = useLang();
  const online = useOnline();
  const queue = useQueue();
  useEffect(() => {
    const onSync = (e) => { const { synced, failed } = e.detail; if (synced) toast.success(t("syncedN").replace("{n}", synced)); if (failed) toast.error(t("syncFailedN").replace("{n}", failed)); };
    window.addEventListener("mohassib:synced", onSync);
    if (online) flushQueue();
    return () => window.removeEventListener("mohassib:synced", onSync);
  }, [online]); // eslint-disable-line
  if (online && queue.length === 0) return null;
  return (
    <div data-testid="offline-bar" className={`no-print flex items-center gap-2 px-4 py-1.5 text-xs font-semibold sm:px-8 ${online ? "bg-sky-50 text-sky-800" : "bg-amber-100 text-amber-900"}`}>
      {online ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <WifiOff className="h-3.5 w-3.5" />}
      {online ? t("syncing") : t("offlineMode")}
      {queue.length > 0 && <span data-testid="offline-queue-count" className="ms-auto rounded-full bg-white/70 px-2 py-0.5">{t("pendingN").replace("{n}", queue.length)}</span>}
    </div>
  );
}
